import React, { useState } from 'react';
import {
  Pencil, Trash2, FileText, Printer,
} from 'lucide-react';
import { notify } from '../../Shared/toast';
import NavigationLinksHr from './NavLinkHr';

const employees = [
  {
    id: 1,
    name: 'Rahim Uddin',
    designation: 'Branch Manager',
    department: 'Operations',
    section: 'Management',
    employmentType: 'Permanent',
    joining: '2022-01-15',
    status: 'Active',
    village: 'Sandira',
    upozila: 'Adamdighi',
    district: 'Bogra',
  },
  {
    id: 2,
    name: 'Salma Akter',
    designation: 'Senior Cashier',
    department: 'Accounts',
    section: 'Finance',
    employmentType: 'Permanent',
    joining: '2023-04-01',
    status: 'On Leave',
    village: 'Nilgonj',
    upozila: 'Kalmakanda',
    district: 'Netrokona',
  },
  {
    id: 3,
    name: 'Nasim Mia',
    designation: 'Cleaner',
    department: 'Maintenance',
    section: 'Housekeeping',
    employmentType: 'Contractual',
    joining: '2024-07-10',
    status: 'Active',
    village: 'Nodikul',
    upozila: 'Naogaon Sadar',
    district: 'Naogaon',
  },
  // Add more mock employees if needed for pagination demo
];

const statuses = ['All', 'Active', 'On Leave', 'Inactive'];
const departments = ['All', 'Operations', 'Accounts', 'Maintenance'];

function EmployeeList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Filter employees based on search and filters
  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || emp.status === statusFilter;
    const matchesDepartment = departmentFilter === 'All' || emp.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePrev = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNext = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-center gap-2 md:gap-3 py-2">
        <NavigationLinksHr />
      </div>
      <div>
        <h2 className="text-2xl font-semibold text-gray-800">👨‍💼 Employee Directory/List</h2>
        <p className="text-sm text-gray-500 mt-1">List of all staff with designation and status.</p>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Search */}
        <input
          type="search"
          placeholder="Search by name..."
          className="border rounded-lg px-3 py-2 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        {/* Filters */}
        <div className="flex gap-3">
          <select
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>

          {/* Export & Print Buttons */}
          <button
            type="button"
            className="flex items-center gap-1 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition text-sm"
            onClick={() => notify.info('Export feature coming soon!')}
          >
            <FileText className="w-4 h-4" />
            Export
          </button>
          <button
            type="button"
            className="flex items-center gap-1 bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition text-sm"
            onClick={() => notify.info('Print feature coming soon!')}
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-white border shadow rounded-2xl overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700 text-left">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Designation</th>
              <th className="px-4 py-3">Department</th>
              <th className="px-4 py-3">Section</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Joining Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Village</th>
              <th className="px-4 py-3">Upzila</th>
              <th className="px-4 py-3">District</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEmployees.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                  No employees found.
                </td>
              </tr>
            ) : (
              paginatedEmployees.map((emp) => (
                <tr key={emp.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{emp.name}</td>
                  <td className="px-4 py-3 text-gray-700">{emp.designation}</td>
                  <td className="px-4 py-3 text-gray-700">{emp.department}</td>
                  <td className="px-4 py-3 text-gray-700">{emp.section}</td>
                  <td className="px-4 py-3 text-gray-600">{emp.employmentType}</td>
                  <td className="px-4 py-3 text-gray-600">{emp.joining}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        // eslint-disable-next-line no-nested-ternary
                        emp.status === 'Active'
                          ? 'bg-green-100 text-green-700'
                          : emp.status === 'On Leave'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {emp.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{emp.village}</td>
                  <td className="px-4 py-3 text-gray-700">{emp.upozila}</td>
                  <td className="px-4 py-3 text-gray-700">{emp.district}</td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        className="text-blue-500 hover:text-blue-700"
                        onClick={() => {
                          notify.info('Edit feature will be added in final production!');
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          notify.info('Delete feature will be added in final production!');
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center text-gray-700 text-sm mt-4">
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={handlePrev}
          className={`px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-100 ${
            currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Previous
        </button>
        <span>
          Page
          {' '}
          <strong>{currentPage}</strong>
          {' '}
          of
          {' '}
          <strong>{totalPages}</strong>
        </span>
        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={handleNext}
          className={`px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-100 ${
            currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default EmployeeList;
